import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AccountingIFrameEvent, AccountingIFrameMethod, toBool } from 'vopay';
import { useAlertDialog, useBlockingLoader } from 'shared';
import { useBranding } from '@/providers/Branding.provider';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import useGenerateAspEmbedUrl from '@/hooks/useApi/useGenerateAspEmbedUrl';
import { useClientData } from '@/providers/ClientData.provider';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';

// The login response mixes '0'/'1' strings and normalized booleans — vopay's
// toBool handles the strings; booleans pass through (toBool(String(true))
// would read 'true' as base-2 and come back false).
function pseudo(value?: string | boolean): '0' | '1' {
  const enabled = typeof value === 'boolean' ? value : toBool(value);

  return enabled ? '1' : '0';
}

type AccountingEmbedPageProps = {
  type: 'Payable' | 'Receivable';
  // Sub-page inside the embedded app (the create pages live in the iframe
  // too); omitted = the section's list.
  page?: 'create-invoice' | 'create-bill';
};

// Hosts the VoPay 360 micro-app (ID-12976): generates the embed URL, shows
// the whole-page loader until the iframe reports READY, pushes branding and
// language through SET_CONFIG, and keeps the portal route in sync with the
// iframe's own navigation — same handshake as DisputeManagementPage and
// GlobalSendPage. Mounted under /accounting/* only, with no menu entry, while
// the new Accounting Software API is validated (ID-13335).
const HOST_BASE_PATH = '/accounting';

export default function AccountingEmbedPage({ type, page }: AccountingEmbedPageProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const blockingLoader = useBlockingLoader();
  const { primaryColor } = useBranding();

  const {
    userSettings: { timezone },
  } = useUserSettingsProvider();

  const history = useHistory();
  const alertDialog = useAlertDialog();
  const { permissions } = usePermissions();
  const { response } = useClientData();
  const { LockedSelectOptions } = useLockedFeatures();
  const { t, i18n } = useTranslation('translations', { keyPrefix: 'AccountingSystem.Embed' });
  const [isReady, setIsReady] = useState(false);
  const { embedUrl, generateAspEmbedUrl } = useGenerateAspEmbedUrl();

  const section = type === 'Receivable' ? 'receivables' : 'payables';
  const embedPath = page ? `${section}/${page}` : section;

  // The same instance survives route changes between /receivables, /payables
  // and the create routes (same component in the same Switch slot), and the
  // message listener below registers once — so it reads the current path
  // through a ref, and portal-driven navigation is pushed to the iframe here.
  const embedPathRef = useRef(embedPath);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (embedPathRef.current === embedPath) return;

    embedPathRef.current = embedPath;

    if (isInitializedRef.current) {
      postMessageToIframe({ type: 'NAVIGATE', payload: { path: embedPath } });
    }
  }, [embedPath]);

  // Balanced against the ref-counted indicator so navigating away mid-boot
  // can't leave the counter held.
  const holdingLoader = useRef(false);

  function holdLoader(on: boolean) {
    if (holdingLoader.current === on) return;

    holdingLoader.current = on;

    if (on) {
      blockingLoader.show();
    } else {
      blockingLoader.hide();
    }
  }

  function postMessageToIframe(message: AccountingIFrameMethod) {
    iframeRef.current?.contentWindow?.postMessage(message, '*');
  }

  useEffect(() => {
    holdLoader(true);
    init();

    return () => holdLoader(false);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    window.addEventListener(
      'message',
      (event: MessageEvent<AccountingIFrameEvent>) => {
        if (!event.data) return;

        if (event.data.type === 'INITIALIZED') {
          isInitializedRef.current = true;

          postMessageToIframe({
            type: 'SET_CONFIG',
            payload: {
              primaryColor,
              timezone,
              language: i18n.language,
              // The portal renders the spinner; the embed forwards its
              // loading state through LOADING messages instead.
              fullScreenLoader: false,
              // The full per-user permission set — the embed is default-closed
              // without it (bill-pay precedent).
              permissions: {
                CreatePayments: permissions.CreatePayments,
                CreateCollections: permissions.CreateCollections,
                CreateElinxPay: permissions.CreateElinxPay,
                CreateElinxCollect: permissions.CreateElinxCollect,
                CreateInvoices: permissions.CreateInvoices,
                CreateBills: permissions.CreateBills,
                ViewBankAccountTransactionHistory: permissions.ViewBankAccountTransactionHistory,
                ViewElinxSettings: permissions.ViewElinxSettings,
              },
              // The account's rail/feature flags, in the login response's
              // pseudo-bool encoding.
              features: {
                EFTCollectEnabled: pseudo(response.EFTCollectEnabled),
                EFTSendEnabled: pseudo(response.EFTSendEnabled),
                ACHFundEnabled: pseudo(response.ACHFundEnabled),
                ACHWithdrawEnabled: pseudo(response.ACHWithdrawEnabled),
                RTRWithdrawEnabled: pseudo(response.RTRWithdrawEnabled),
                RTPWithdrawEnabled: pseudo(response.RTPWithdrawEnabled),
                InteracMoneyRequestEnabled: pseudo(response.InteracMoneyRequestEnabled),
                InteracBulkPayoutEnabled: pseudo(response.InteracBulkPayoutEnabled),
                PushToCardEnabled: pseudo(response.PushToCardEnabled),
                CreditCardEnabled: pseudo(response.CreditCardEnabled),
                PayLinkEnabled: pseudo(response.PayLinkEnabled),
                IQ11StandardEnabled: pseudo(response.IQ11StandardEnabled),
                IQ11BoostEnabled: pseudo(response.IQ11BoostEnabled),
                IQ11PlusEnabled: pseudo(response.IQ11PlusEnabled),
                IQ11AdvantageEnabled: pseudo(response.IQ11AdvantageEnabled),
                IQ11DataEnrichingEnabled: pseudo(response.IQ11DataEnrichingEnabled),
                IQ11DataEnrichingEnhancedEnabled: pseudo(response.IQ11DataEnrichingEnhancedEnabled),
                MicroDepositBankAccountVerificationEnabled: pseudo(response.MicroDepositBankAccountVerificationEnabled),
                InteracBasicVerificationEnabled: pseudo(response.InteracBasicVerificationEnabled),
                InteracEnhancedVerificationEnabled: pseudo(response.InteracEnhancedVerificationEnabled),
                CardBasicVerificationEnabled: pseudo(response.CardBasicVerificationEnabled),
                CardEnhancedVerificationEnabled: pseudo(response.CardEnhancedVerificationEnabled),
              },
              lockedSelectOptions: LockedSelectOptions,
            },
          });

          postMessageToIframe({ type: 'NAVIGATE', payload: { path: embedPathRef.current } });
        }

        if (event.data.type === 'READY') {
          setIsReady(true);
          holdLoader(false);
        }

        if (event.data.type === 'LOADING') {
          holdLoader(!event.data.payload.completed);
        }

        if (event.data.type === 'NAVIGATED') {
          // Pre-handshake NAVIGATED is boot noise: the iframe's router
          // redirects '' -> receivables before the token exchange finishes,
          // and reacting to it would hijack a /payables deep link. The host
          // pushes the correct section itself right after INITIALIZED.
          if (!isInitializedRef.current) return;

          const path = event.data.payload.path;
          const hostedPaths = ['receivables', 'payables', 'receivables/create-invoice', 'payables/create-bill'];

          if (hostedPaths.includes(path) && path !== embedPathRef.current) {
            // Take the ref along so the sync effect doesn't echo the iframe's
            // own navigation back at it.
            embedPathRef.current = path;
            history.push(`${HOST_BASE_PATH}/${path}`);
          } else if (path.startsWith('/')) {
            // Absolute path = a link-out from inside the embed (transaction
            // details, support tickets, ...) — the portal navigates itself.
            history.push(path);
          } else if (path.startsWith('?')) {
            // Query-only path = a link-out relative to the page the portal is
            // already on (the embed's "Go to Activity" deep-links into the
            // current contact's tabs), so keep the pathname and swap the search.
            history.push({ pathname: history.location.pathname, search: path });
          }
        }
      },
      { signal: controller.signal },
    );

    return () => controller.abort();
  }, []);

  async function init() {
    try {
      await generateAspEmbedUrl({ Language: i18n.language });
    } catch (err) {
      holdLoader(false);

      alertDialog.error(
        t('loadErrorTitle', { defaultValue: 'Something went wrong' }),
        err instanceof Error ? err.message : t('loadError', { defaultValue: 'The page could not be loaded.' }),
      );
    }
  }

  return (
    <>
      {embedUrl && (
        <iframe
          ref={iframeRef}
          src={embedUrl}
          title={t('iframeTitle', { defaultValue: 'Receivables and Payables' })}
          style={{ width: '100%', height: '100%', border: 'none', visibility: isReady ? 'visible' : 'hidden' }}
          allow="clipboard-write"
          seamless
        ></iframe>
      )}
    </>
  );
}
